import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const revenueModels = [
    'subscription',
    'transactional'
]

const RevenueValueDriverSalesForm = forwardRef(({ currency, outcome, outcomeList, editSalesDriverId, editSalesDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        outboundConversion: "Outbound to meeting conversion measures the percentage of outbound contacts that result in scheduled meetings, which is the effectiveness of your outreach efforts in engaging prospects.",
        outboundConversionChange: `${positiveDriver ? "Increased" : "Decreased"} outbound to meeting conversion rate when using your product campared to the competitive alternative.`,
        inboundConversion: "Inbound to meeting conversion measures the percentage of inbound leads that progress to scheduled meetings, reflecting the effectiveness of your lead capture and nurturing process in engaging potential customers.",
        inboundConversionChange: `${positiveDriver ? "Increased" : "Decreased"} inbound to meeting conversion rate when using your product campared to the competitive alternative.`,
        demoConversion: "Meeting to Demo conversion measures the percentage of meetings that progress into product demonstrations, reflecting the effectiveness of turning initial engagements into opportunities to showcase your solution.",
        demoConversionChange: `${positiveDriver ? "Increased" : "Decreased"} meeting to demo conversion rate when using your product campared to the competitive alternative.`,
        salesConversion: "Demo to Sales conversion measures the percentage of product demonstrations that result in a closed sale, reflecting the effectiveness of your demos in converting prospects into customers.",
        salesConversionChange: `${positiveDriver ? "Increased" : "Decreased"} demo to sales conversion rate when using your product campared to the competitive alternative.`,

    }

    const handleRevenueModelChange = (event) => {
        formik.setFieldValue('revenue_model', event.target.value)
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editSalesDriverId) {
            setPositiveDriver(editSalesDriver["positive_driver"])

            formik.setFieldValue("conversion", editSalesDriver["conversion"])
            formik.setFieldValue("conversion_increase", editSalesDriver["conversion_increase"])
            formik.setFieldValue("revenue_model", editSalesDriver["revenue_model"])
            formik.setFieldValue("annual_contract_value", editSalesDriver["annual_contract_value"])
            formik.setFieldValue("transaction_value", editSalesDriver["transaction_value"])
            formik.setFieldValue("transaction_frequency", editSalesDriver["transaction_frequency"])
            formik.setFieldValue("contribution_margin", editSalesDriver["contribution_margin"])
            formik.setFieldValue("positive_driver", editSalesDriver["positive_driver"])
            formik.setFieldValue("outcome", editSalesDriver["outcome"])
            formik.setFieldValue("driver_name", editSalesDriver["driver_name"])
            formik.setFieldValue("comment", editSalesDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editSalesDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `Conversion ${positiveDriver ? 'Increase' : 'Decrease'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const conversionInitialValues = {
        inbound: 5,
        outbound: 2,
        demo: 50,
        sales: 50,
    }

    const conversionIncreaseInitialValues = {
        inbound: 0,
        outbound: 0,
        demo: 0,
        sales: 0,
    }

    const initialValues = {
        id: null,
        revenue_type: 'conversion',
        outcome: '',
        driver_name: `Conversion ${positiveDriver ? 'Increase' : 'Decrease'}`,
        comment: '',
        positive_driver: true,
        type: '',
        currency: currency,
        conversion: conversionInitialValues,
        conversion_increase: conversionIncreaseInitialValues,
        annual_contract_value: 0,
        transaction_value: 0,
        transaction_frequency: 0,
        contribution_margin: 0,
        revenue_model: revenueModels[0]
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {
            conversion: {},
            conversion_increase: {}
        };

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        Object.keys(conversionInitialValues).forEach(type => {
            if (!isNumeric(values.conversion[type])) {
                errors.conversion[type] = 'Must be numeric value';
            } else if (!isPositive(values.conversion[type])) {
                errors.conversion[type] = 'Must be positive or zero';
            } else if (values.conversion[type] > 100) {
                errors.conversion[type] = 'Not more than 100';
            }
        })

        Object.keys(conversionIncreaseInitialValues).forEach(type => {
            if (!isNumeric(values.conversion_increase[type])) {
                errors.conversion_increase[type] = 'Must be numeric value';
            } else if (!isPositive(values.conversion_increase[type])) {
                errors.conversion_increase[type] = 'Must be positive or zero';
            } else if (values.conversion_increase[type] > 100) {
                errors.conversion_increase[type] = 'Not more than 100';
            }
        })

        if (!isNumeric(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be numeric value';
        } else if (!isPositive(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.contribution_margin)) {
            errors.contribution_margin = 'Must be numeric value';
        } else if (!isPositive(values.contribution_margin)) {
            errors.contribution_margin = 'Must be positive or zero';
        } else if (values.contribution_margin > 100) {
            errors.contribution_margin = 'Not more than 100';
        }

        if (!isNumeric(values.transaction_value)) {
            errors.transaction_value = 'Must be numeric value';
        } else if (!isPositive(values.transaction_value)) {
            errors.transaction_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be numeric value';
        } else if (!isPositive(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be positive or zero';
        }

        // Filter out empty nested error objects for efficiency_gain_contact and min_per_contact
        if (Object.keys(errors.conversion).length === 0) {
            delete errors.conversion;
        }
        if (Object.keys(errors.conversion_increase).length === 0) {
            delete errors.conversion_increase;
        }

        // If the errors object is empty (meaning no errors), return an empty object
        if (Object.keys(errors).length === 0) {
            return {};
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            setPositiveDriver(true)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1300 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            1. Conversion
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Outbound to Meeting conversion{tooltip(tooltipText.outboundConversion)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`Outbound conversion ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.outboundConversionChange)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion.outbound"
                                endAdornment="%"
                                value={formik.values.conversion.outbound}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion.outbound", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion.outbound") && Boolean(getIn(formik.errors, "conversion.outbound"))}
                                helperText={getIn(formik.touched, "conversion.outbound") && getIn(formik.errors, "conversion.outbound")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion_increase.outbound"
                                endAdornment="%"
                                value={formik.values.conversion_increase.outbound}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion_increase.outbound", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion_increase.outbound") && Boolean(getIn(formik.errors, "conversion_increase.outbound"))}
                                helperText={getIn(formik.touched, "conversion_increase.outbound") && getIn(formik.errors, "conversion_increase.outbound")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                        <Grid item xs={3} />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Inbound to Meeting conversion{tooltip(tooltipText.inboundConversion)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`Inbound conversion ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.inboundConversionChange)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion.inbound"
                                endAdornment="%"
                                value={formik.values.conversion.inbound}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion.inbound", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion.inbound") && Boolean(getIn(formik.errors, "conversion.inbound"))}
                                helperText={getIn(formik.touched, "conversion.inbound") && getIn(formik.errors, "conversion.inbound")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion_increase.inbound"
                                endAdornment="%"
                                value={formik.values.conversion_increase.inbound}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion_increase.inbound", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion_increase.inbound") && Boolean(getIn(formik.errors, "conversion_increase.inbound"))}
                                helperText={getIn(formik.touched, "conversion_increase.inbound") && getIn(formik.errors, "conversion_increase.inbound")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                            <Grid item xs={6} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Meeting to Demo conversion{tooltip(tooltipText.demoConversion)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`Demo conversion ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.demoConversionChange)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion.demo"
                                endAdornment="%"
                                value={formik.values.conversion.demo}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion.demo", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion.demo") && Boolean(getIn(formik.errors, "conversion.demo"))}
                                helperText={getIn(formik.touched, "conversion.demo") && getIn(formik.errors, "conversion.demo")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion_increase.demo"
                                endAdornment="%"
                                value={formik.values.conversion_increase.demo}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion_increase.demo", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion_increase.demo") && Boolean(getIn(formik.errors, "conversion_increase.demo"))}
                                helperText={getIn(formik.touched, "conversion_increase.demo") && getIn(formik.errors, "conversion_increase.demo")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Demo to Sales conversion{tooltip(tooltipText.salesConversion)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`Sales conversion ${positiveDriver ? 'Increase' : 'Decrease'}`}{tooltip(tooltipText.salesConversionChange)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion.sales"
                                endAdornment="%"
                                value={formik.values.conversion.sales}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion.sales", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion.sales") && Boolean(getIn(formik.errors, "conversion.sales"))}
                                helperText={getIn(formik.touched, "conversion.sales") && getIn(formik.errors, "conversion.sales")}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="conversion_increase.sales"
                                endAdornment="%"
                                value={formik.values.conversion_increase.sales}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("conversion_increase.sales", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "conversion_increase.sales") && Boolean(getIn(formik.errors, "conversion_increase.sales"))}
                                helperText={getIn(formik.touched, "conversion_increase.sales") && getIn(formik.errors, "conversion_increase.sales")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Profit per customer
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption">
                            Your Customers' revenue model{tooltip(generalTooltipText.revenueModel)}
                        </Typography>
                        <Box>
                            <Select
                                open={open}
                                onClose={handleClose}
                                onOpen={handleOpen}
                                value={formik.values.revenue_model}
                                onChange={handleRevenueModelChange}
                                size='small'
                            >
                                {revenueModels.map((model) => (
                                    <MenuItem key={model} value={model}>
                                        {firstCharToUpper(model)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {formik.values.revenue_model === 'subscription' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Annual Contract Value (ACV){tooltip(generalTooltipText.acv)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="annual_contract_value"
                                        startAdornment={currency}
                                        value={formik.values.annual_contract_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("annual_contract_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "annual_contract_value") && Boolean(getIn(formik.errors, "annual_contract_value"))}
                                        helperText={getIn(formik.touched, "annual_contract_value") && getIn(formik.errors, "annual_contract_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                {formik.values.revenue_model === 'transactional' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction value{tooltip(generalTooltipText.transactionValue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction frequency{tooltip(generalTooltipText.transactionFrequency)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_value"
                                        startAdornment={currency}
                                        value={formik.values.transaction_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_value") && Boolean(getIn(formik.errors, "transaction_value"))}
                                        helperText={getIn(formik.touched, "transaction_value") && getIn(formik.errors, "transaction_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_frequency"
                                        endAdornment={
                                            <Typography variant="caption">
                                                times/year
                                            </Typography>
                                        }
                                        value={formik.values.transaction_frequency}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_frequency", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_frequency") && Boolean(getIn(formik.errors, "transaction_frequency"))}
                                        helperText={getIn(formik.touched, "transaction_frequency") && getIn(formik.errors, "transaction_frequency")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            3. Driver details
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {!outcome &&
                    (<React.Fragment>
                        <Grid item xs={2}>
                            <Stack direction="column" gap={1}>
                                <Typography variant="caption">
                                    Outcome
                                </Typography>
                                <Box>
                                    <Select
                                        open={openOutcome}
                                        onClose={handleOutcomeClose}
                                        onOpen={handleOutcomeOpen}
                                        value={formik.values.outcome}
                                        onChange={handleOutcomeChange}
                                        size='small'
                                    >
                                        {outcomeList.map((outcome) => (
                                            <MenuItem key={outcome} value={outcome}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {outcome}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={10} />
                    </React.Fragment>)
                }
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RevenueValueDriverSalesForm                